import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { User } from 'firebase/auth';
import { useCurrentUser } from 'app';
import { useUserRole } from 'utils/useUserRole';

// Define the shape of our auth context
interface AuthContextType {
  user: User | null;
  role: string | null;
  loading: boolean;
  error: Error | null;
}

// Create the context
const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Provider component
interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const { user, loading: authLoading } = useCurrentUser();
  const { role, loading: roleLoading, error } = useUserRole({ user, authLoading });
  
  // Combined loading state - we're loading if either auth or role is loading
  const loading = authLoading || roleLoading;

  const value: AuthContextType = {
    user,
    role,
    loading,
    error,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

// Hook to use the auth context
export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
